// IdePage.js
import React, { useState, useRef, useEffect } from 'react';
import './IdePage.css';
import IDE from './IDE';
import { useLocation, useNavigate } from 'react-router-dom';
import { PROBLEMS, IDE_PROBLEM_ID } from './problems';

function TraceTable({ traceVars = [], variableNames = [] }) {
  const getCell = (c) =>
    typeof c === 'object' && c && 'value' in c ? String(c.value ?? '') : String(c ?? '');

  const maxCols = Math.max(
    variableNames.length,
    ...(traceVars.map((r) => r.length))
  );
  const headers = Array.from({ length: maxCols }, (_, i) => variableNames[i] ?? `var${i + 1}`);

  return (
    <table className="trace-table">
      <thead>
        <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {traceVars.map((row, ri) => (
          <tr key={ri}>
            {headers.map((_, ci) => (
              <td key={ci}><code>{getCell(row[ci] ?? '')}</code></td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IdePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { steps: stepsFromNav, variableNames: namesFromNav, problemId: navProblemId } = location.state || {};
  const storedProblemId = localStorage.getItem('rt_active_ide_problem');
  const ideProblemId = navProblemId || storedProblemId || IDE_PROBLEM_ID;
  const problemConfig = PROBLEMS[ideProblemId] || PROBLEMS[IDE_PROBLEM_ID];

  const stepsStorageKey = `rt_${ideProblemId}_steps`;
  const varStorageKey = `rt_${ideProblemId}_varnames`;
  const storedSteps = JSON.parse(localStorage.getItem(stepsStorageKey) || localStorage.getItem('rt_steps') || '[]');
  const storedVars = JSON.parse(localStorage.getItem(varStorageKey) || localStorage.getItem('rt_varnames') || '["nums","var","var2","var3"]');
  const steps = stepsFromNav ?? storedSteps;
  const variableNames = namesFromNav ?? storedVars;

  const leftPaneRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem('rt_active_ide_problem', ideProblemId);
    } catch {}
  }, [ideProblemId]);

  useEffect(() => {
    try {
      localStorage.setItem('rt_flow_stage', 'ide');
      localStorage.setItem('rt_last_route', '/ide');
    } catch {}
  }, []);
  
  // ---- FIX: initialize a concrete width on first paint (never null) ----
  const defaultLeft = Math.max(300, Math.floor(window.innerWidth / 2));
  const [leftWidth, setLeftWidth] = useState(() => {
    const raw = localStorage.getItem('rt_ide_left_width');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : defaultLeft;
  });
  const [stepsCollapsed, setStepsCollapsed] = useState(false);

  // confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingCode, setPendingCode] = useState('');

  const starterCode = problemConfig.starterCode;
  // Use starterCode unless a non-empty, non-null/undefined string is saved
const [code, setCode] = useState(() => {
  try {
    const raw = localStorage.getItem('rt_ide_code');
    if (raw && raw !== 'null' && raw !== 'undefined' && raw.trim() !== '') {
      return raw;
    }
  } catch {}
  return starterCode;
});

// (Optional but recommended) One-time migration to clean old bad values
useEffect(() => {
  try {
    const raw = localStorage.getItem('rt_ide_code');
    if (!raw || raw === 'null' || raw === 'undefined' || raw.trim() === '') {
      setCode(starterCode);                // populate editor
      localStorage.setItem('rt_ide_code', starterCode); // persist immediately
    }
  } catch {}
  // run once
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [starterCode]);

useEffect(() => {
  try { localStorage.setItem('rt_ide_code', code); } catch {}
}, [code]);

  const handleMouseDown = (e) => {
    if (stepsCollapsed) return;
    e.preventDefault();
    const startX = e.clientX;
    const container = leftPaneRef.current?.parentElement;
    const containerWidth = container?.getBoundingClientRect().width ?? window.innerWidth;
    const startWidth = leftPaneRef.current?.getBoundingClientRect().width ?? leftWidth;

    const onMouseMove = (me) => {
      const raw = startWidth + (me.clientX - startX);
      const min = 200, max = containerWidth - 200;
      const w = Math.min(Math.max(raw, min), max);
      setLeftWidth(w);
      try { localStorage.setItem('rt_ide_left_width', String(w)); } catch {}
    };

    document.body.style.userSelect = 'none';
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // open confirm modal instead of navigating immediately
  const openSubmitConfirm = () => {
    setPendingCode(code);
    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    try {
      await fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'submit_ide_code', details: { code: pendingCode, problem_id: ideProblemId } }),
        credentials: 'include',
      });
    } catch (error) {
      console.error("Logging failed:", error);
    }
    setShowConfirm(false);
    try {
      localStorage.setItem('rt_flow_stage', 'post_survey');
      localStorage.setItem('rt_last_route', '/post-survey');
    } catch {}
    navigate('/post-survey');
    
  };

  const cancelSubmit = () => {
    setShowConfirm(false);
    setPendingCode('');
  };

  // close modal on Escape
  useEffect(() => {
    if (!showConfirm) return;
    const onKey = (e) => { if (e.key === 'Escape') cancelSubmit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showConfirm]);

  return (
    <div className="ide-page-shell">
      <div className="trace-toggle-anchor">
        <button
          className="trace-toggle-btn"
          type="button"
          onClick={() => setStepsCollapsed((prev) => !prev)}
        >
          {stepsCollapsed ? 'Show Trace View' : 'Hide Trace View'}
        </button>
      </div>
      <div className="ide-page-layout">
      <div
        className="ide-pane"
        ref={leftPaneRef}
        style={{ flex: stepsCollapsed ? '1 1 100%' : `0 0 ${leftWidth}px` }}
      >
        {/* Ensure IDE root fills height via CSS (height: 100%) */}
        <IDE
          value={code}
          onChange={setCode}
          onSubmit={openSubmitConfirm}
          showInlineSubmit={stepsCollapsed}
        />
      </div>

      {!stepsCollapsed && (
        <div className="vertical-resizer" onMouseDown={handleMouseDown} />
      )}

      {!stepsCollapsed && (
      <div className="steps-pane">
        <div className="steps-pane-header">
          <h2>{problemConfig.title} — Your Steps</h2>
        </div>
        <div className="steps-pane-content">
          {steps.length === 0 ? (
            <div className="no-steps-message">
              No steps yet. Go back and submit your solution to see them here.
            </div>
          ) : (
            steps.map((step, i) => (
              <div
                key={i}
                className="step-display-container"
                style={{ marginLeft: `${Number(step?.indent ?? 0) * 50}px` }}
              >
                <div className="step-description-display">
                  <strong>Step {i + 1}:</strong> {step.description || ''}
                </div>

                {Array.isArray(step.traceVars) && step.traceVars.length > 0 && (
                  <div className="step-trace-display">
                    <TraceTable traceVars={step.traceVars} variableNames={variableNames} />
                  </div>
                )}

                {step.simulation ? (
                  <div className="step-simulation-display">
                    <pre>{step.simulation}</pre>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
        <div className="steps-pane-footer">
          <button className="steps-submit-btn" type="button" onClick={openSubmitConfirm}>
            Submit
          </button>
        </div>
      </div>
      )}
      </div>

      {/* confirmation modal */}
      {showConfirm && (
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) cancelSubmit(); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="submit-confirm-title"
          aria-describedby="submit-confirm-desc"
        >
          <div className="modal-content">
            <h3 id="submit-confirm-title">Submit your solution?</h3>
            <p id="submit-confirm-desc">
              This action is <strong>irreversible</strong>. After you submit, you won’t be able to edit your steps or code.
            </p>
            <div className="modal-actions">
              <button className="modal-cancel-btn" onClick={cancelSubmit}>Cancel</button>
              <button className="modal-confirm-btn" onClick={confirmSubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default IdePage;
